// SPDX-License-Identifier: Apache-2.0

import PerunMessage from "./PerunMessage";
import { MessageType, MessageTypeName } from "./messageTypes";
import { jsonArrayMember, jsonMember, jsonObject } from "typedjson";
import {
  Asset,
  serializeAsset,
  deserializeAsset,
} from "./types/channelState";
import EthereumAsset, { AssetID } from "./types/ethereumAsset";

@jsonObject()
export class AssetGuardrail {
  @jsonMember({
    deserializer: deserializeAsset,
    serializer: serializeAsset,
  })
  asset: Asset;
  @jsonMember(Number) price: number;
  @jsonMember(String) threshold: string;

  constructor(asset: Asset, price: number, threshold: string) {
    this.asset = asset;
    this.price = price;
    this.threshold = threshold;
  }
}

@jsonObject()
export default class GetGuardrailsResponse extends PerunMessage {
  @jsonArrayMember(AssetGuardrail) guardrails: AssetGuardrail[];

  constructor(guardrails: AssetGuardrail[]) {
    super();
    this.guardrails = guardrails;
  }

  messageType(): MessageType {
    return GetGuardrailsResponse;
  }

  messageTypeName(): MessageTypeName {
    return "GetGuardrailsResponse";
  }
}

/**
 * Indexes a guardrail response by the ID of the asset that was asked about.
 *
 * The guardrail payload carries assetHolder and chainID but no token address,
 * so deserializeAsset substitutes the zero address. assetID() is built from
 * that address, so every Ethereum asset on a chain collapses to one key and
 * whichever the hub returns last wins — and the hub returns them in Go map
 * order, so the winner changes between page loads. Key off the asset we asked
 * about instead: assetHolder is always present and is unique per asset.
 *
 * Stellar and Nervos assets survive the round trip intact and are keyed by
 * their own ID.
 */
export function indexGuardrailsByAsset(
  guardrails: AssetGuardrail[],
  requested: Asset[],
): Map<AssetID, AssetGuardrail> {
  const byAsset = new Map<AssetID, AssetGuardrail>();
  guardrails.forEach((g) => {
    if (g.asset instanceof EthereumAsset) {
      const returned = g.asset;
      const holder = returned.assetHolder.toString();
      const match = requested.find(
        (a): a is EthereumAsset =>
          a instanceof EthereumAsset &&
          a.assetHolder.toString() === holder &&
          a.chainID === returned.chainID,
      );
      if (match) {
        byAsset.set(match.assetID(), g);
      } else {
        console.error("guardrail for an asset that was not requested:", holder);
      }
    } else {
      byAsset.set(g.asset.assetID(), g);
    }
  });
  return byAsset;
}
